import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Mail, Eye, EyeOff, LogIn, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../config/api';

// Composant Logo AbracadaBati
function LogoBatiAssist({ className = "h-12" }) {
  return (
    <img 
      src="https://customer-assets.emergentagent.com/job_organize-smart/artifacts/4450nlnf_abracadabait_logo_transparent.png" 
      alt="AbracadaBati"
      className={className}
    />
  );
}

export default function ClientLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Use XMLHttpRequest to avoid postMessage/DataCloneError from external scripts
    const loginUrl = `${API_URL}/api/client/auth/login?email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`;
    
    const xhr = new XMLHttpRequest();
    xhr.open('POST', loginUrl, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    
    xhr.onreadystatechange = function() {
      if (xhr.readyState === 4) {
        setIsLoading(false);
        
        if (xhr.status === 200) {
          try {
            const data = JSON.parse(xhr.responseText);
            localStorage.setItem('client_token', data.token);
            localStorage.setItem('client_user', JSON.stringify(data.user));
            toast.success(`Bienvenue ${data.user.name} !`);
            navigate('/espace-client/dashboard');
          } catch (parseErr) {
            setError('Erreur lors de la connexion');
            toast.error('Erreur lors de la connexion');
          }
        } else {
          let errorMsg = 'Email ou mot de passe incorrect';
          try {
            const errorData = JSON.parse(xhr.responseText);
            errorMsg = errorData.detail || errorMsg;
          } catch (e) {}
          setError(errorMsg);
          toast.error(errorMsg);
        }
      }
    };
    
    xhr.onerror = function() {
      setIsLoading(false);
      setError('Erreur de connexion au serveur');
      toast.error('Erreur de connexion au serveur');
    };
    
    xhr.send();
  };

  return (
    <div className="min-h-screen min-h-[100dvh] bg-gradient-to-br from-ba-primary/5 via-ba-accent/10 to-white flex items-center justify-center p-4 sm:p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-5 sm:mb-6">
          <div className="flex justify-center mb-2 sm:mb-3">
            <LogoBatiAssist className="h-14 sm:h-16" />
          </div>
          <h1 className="text-lg sm:text-xl font-bold text-rm-text">Cockpit Professionnel</h1>
          <p className="text-sm text-rm-text-secondary">Entre dans ton centre de pilotage</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl p-5 sm:p-6 md:p-8">
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 border border-red-200 rounded-xl p-3 sm:p-4 flex items-center gap-3"
              >
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-600">{error}</p>
              </motion.div>
            )}

            <div>
              <label className="block text-sm font-medium text-rm-text mb-2">
                Adresse email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 sm:py-3.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-ba-primary/20 focus:border-ba-primary transition-colors text-base touch-manipulation"
                  placeholder="ton@email.com"
                  autoComplete="email"
                  data-testid="client-login-email"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-rm-text mb-2">
                Mot de passe
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 sm:py-3.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-ba-primary/20 focus:border-ba-primary transition-colors text-base touch-manipulation"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  data-testid="client-login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1 touch-manipulation"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-ba-primary hover:bg-ba-primary/90 text-white font-semibold rounded-xl py-3.5 sm:py-4 transition-all touch-manipulation active:scale-98"
              data-testid="client-login-submit"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Connexion...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <LogIn className="w-5 h-5" />
                  Se connecter
                </span>
              )}
            </Button>
          </form>

          <div className="mt-5 sm:mt-6 text-center">
            <p className="text-sm text-rm-text-secondary">
              Pas encore d'acces ?{' '}
              <a 
                href="https://calendly.com/romain-trallero/diagnostic-transition-digitale-60min"
                target="_blank"
                rel="noopener noreferrer"
                className="text-ba-primary hover:underline font-medium"
              >
                Contacte-nous
              </a>
            </p>
          </div>
        </div>

        {/* Back to site */}
        <div className="text-center mt-5 sm:mt-6">
          <a href="/" className="text-sm text-rm-text-secondary hover:text-ba-primary transition-colors touch-manipulation py-2 inline-block">
            ← Retour au site
          </a>
        </div>
      </motion.div>
    </div>
  );
}
